/**
 * Cloudflare Turnstile — invisible CAPTCHA for the free scan form + signup.
 *
 * The widget runs in `invisible` mode: it loads silently in the background
 * and only challenges visitors that look like bots. Real users never see
 * anything; abusers get an interactive challenge.
 *
 * Env-gated: if VITE_TURNSTILE_SITE_KEY is not set we silently no-op
 * (`getTurnstileToken()` resolves to `null`). That means deploying this
 * code without setting up Turnstile breaks nothing — the site key flips
 * protection on the moment it's set.
 *
 * Setup:
 *   1. Cloudflare → Turnstile → Add Site → "inboxguard.io" → Invisible.
 *   2. Set `VITE_TURNSTILE_SITE_KEY=<sitekey>` at build time.
 *   3. Set `TURNSTILE_SECRET_KEY` (the matching secret) on the API stack
 *      via Secrets Manager and surface it as an env var on the
 *      scan-domain + turnstile-verify lambdas.
 */
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: string | HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          size?: 'invisible' | 'normal' | 'compact';
          execution?: 'render' | 'execute';
          appearance?: 'always' | 'execute' | 'interaction-only';
        },
      ) => string;
      execute: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      remove: (widgetId: string) => void;
    };
  }
}

export function turnstileEnabled(): boolean {
  return typeof SITE_KEY === 'string' && SITE_KEY.length > 0;
}

let scriptLoadingPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptLoadingPromise) return scriptLoadingPromise;
  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(s);
  });
  return scriptLoadingPromise;
}

/**
 * Get a one-time Turnstile token for the current user. Resolves to:
 *   - a token string when Turnstile is enabled and the user passes
 *   - `null` when Turnstile isn't configured (env-gated)
 *
 * Throws if Turnstile is enabled and the challenge is failed/blocked. We
 * surface that as a normal exception so callers can show a "couldn't verify"
 * message and retry.
 *
 * Internally the widget is rendered into a hidden div that we create on
 * demand, executed once, and then removed. This keeps the API call-site
 * small (no JSX), at the cost of a brief script + iframe load on first use.
 */
export async function getTurnstileToken(action?: string): Promise<string | null> {
  if (!turnstileEnabled() || !SITE_KEY) return null;
  await loadScript();
  if (!window.turnstile) throw new Error('Turnstile failed to initialize');

  // Container is recreated per call so we can throw it away cleanly.
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '1px';
  container.style.height = '1px';
  document.body.appendChild(container);

  return new Promise<string | null>((resolve, reject) => {
    let widgetId: string | null = null;
    const cleanup = () => {
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // ignore — widget already gone
        }
      }
      container.remove();
    };
    try {
      widgetId = window.turnstile!.render(container, {
        sitekey: SITE_KEY,
        size: 'invisible',
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => {
          cleanup();
          resolve(token);
        },
        'error-callback': () => {
          cleanup();
          reject(new Error('Turnstile challenge failed'));
        },
      });
      // execution: 'execute' means render() doesn't auto-fire — we call execute
      // explicitly. Action label is logged in the Turnstile analytics dashboard.
      if (widgetId) window.turnstile!.execute(widgetId);
      else {
        cleanup();
        reject(new Error('Turnstile widget could not be created'));
      }
    } catch (err) {
      cleanup();
      reject(err);
    }
    // Belt-and-suspenders timeout — if the network blocks Turnstile silently
    // (corporate proxy, ad blocker), don't hang the form forever.
    setTimeout(() => {
      cleanup();
      // Resolve null instead of reject so a network-blocked user can still
      // attempt the action. The server-side verify is the real gate.
      resolve(null);
    }, 15_000);
    // Reference action so it shows up in Turnstile analytics. The widget
    // doesn't accept it as a render param so we just attach it to the dataset.
    container.dataset.action = action ?? 'unknown';
  });
}
